// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ResetPasswordPage
 * Handles two flows:
 *   1. /reset-password                   → "Forgot password" — enter email
 *   2. /reset-password?token=<TOKEN>     → set new password
 */
export default function ResetPasswordPage() {
  const [step, setStep] = useState('loading'); // loading | request | set | success | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      setToken(t);
      validateToken(t);
    } else {
      setStep('request');
    }
  }, []);

  const validateToken = async (t) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/reset-password/validate?token=${t}`);
      setMaskedEmail(res.data.masked_email || '');
      setStep('set');
    } catch (e) {
      setError(e.response?.data?.detail || 'This reset link is invalid or has expired.');
      setStep('error');
    }
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setMessage(res.data.message);
      setStep('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setMessage(res.data.message);
      setStep('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    }
    setLoading(false);
  };

  const strengthInfo = (pwd) => {
    if (!pwd) return { label: '', color: 'bg-gray-200', width: '0%' };
    if (pwd.length < 6) return { label: 'Too short', color: 'bg-red-400', width: '20%' };
    if (pwd.length < 8) return { label: 'Weak', color: 'bg-orange-400', width: '40%' };
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[^A-Za-z0-9]/.test(pwd);
    const score = [hasUpper, hasNumber, hasSymbol].filter(Boolean).length;
    if (score === 0) return { label: 'Fair', color: 'bg-yellow-400', width: '55%' };
    if (score === 1) return { label: 'Good', color: 'bg-blue-400', width: '70%' };
    if (score === 2) return { label: 'Strong', color: 'bg-green-500', width: '85%' };
    return { label: 'Very Strong', color: 'bg-green-600', width: '100%' };
  };

  const strength = strengthInfo(password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-6 text-center">
          <h1 className="text-2xl font-bold text-white">PricePAL Compare</h1>
          <p className="text-green-100 text-sm mt-1">
            {step === 'request' && 'Reset your password'}
            {step === 'set' && 'Create a new password'}
            {step === 'success' && 'All done!'}
            {step === 'error' && 'Link expired'}
          </p>
        </div>

        <div className="px-8 py-8">
          {/* ── Loading ── */}
          {step === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Verifying your link…</p>
            </div>
          )}

          {/* ── Error (expired/invalid link) ── */}
          {step === 'error' && (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">⚠️</div>
              <p className="text-red-600 font-semibold mb-2">Reset link problem</p>
              <p className="text-gray-500 text-sm mb-6">{error}</p>
              <a
                href="/app"
                className="block w-full bg-green-600 text-white py-3 rounded-xl font-semibold text-center hover:bg-green-700 transition mb-3"
              >
                Request a new link
              </a>
              <a href="/app" className="text-sm text-gray-400 hover:text-gray-600">← Back to login</a>
            </div>
          )}

          {/* ── Step 1: Enter email to request reset ── */}
          {step === 'request' && (
            <form onSubmit={handleRequest} className="space-y-5">
              <div className="text-center mb-2">
                <div className="text-4xl mb-3">🔑</div>
                <p className="text-gray-600 text-sm">
                  Enter the email address for your account and we'll send you a password reset link.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-800"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><span className="animate-spin">⏳</span> Sending…</>
                ) : (
                  '📧 Send Reset Link'
                )}
              </button>

              <div className="text-center">
                <a href="/app" className="text-sm text-green-600 hover:text-green-700 font-medium">
                  ← Back to login
                </a>
              </div>
            </form>
          )}

          {/* ── Step 2: Enter new password ── */}
          {step === 'set' && (
            <form onSubmit={handleReset} className="space-y-5">
              {maskedEmail && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800 text-center">
                  Resetting password for <strong>{maskedEmail}</strong>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    required
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {/* Strength bar */}
                {password && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                        style={{ width: strength.width }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{strength.label}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your new password"
                  required
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    confirmPassword && confirmPassword !== password
                      ? 'border-red-300 bg-red-50'
                      : confirmPassword && confirmPassword === password
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300'
                  }`}
                />
                {confirmPassword && confirmPassword === password && (
                  <p className="text-xs text-green-600 mt-1">✓ Passwords match</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !password || !confirmPassword}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><span className="animate-spin">⏳</span> Updating…</>
                ) : (
                  '🔒 Set New Password'
                )}
              </button>
            </form>
          )}

          {/* ── Success ── */}
          {step === 'success' && (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {token ? 'Password Updated!' : 'Check your email'}
              </h3>
              <p className="text-gray-500 text-sm mb-6">{message}</p>

              {token ? (
                <a
                  href="/app"
                  className="block w-full bg-green-600 text-white py-3 rounded-xl font-semibold text-center hover:bg-green-700 transition"
                >
                  Log in with new password →
                </a>
              ) : (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                    <p className="font-medium mb-1">📧 Email sent!</p>
                    <p>Check your inbox (and spam folder). The link expires in 2 hours.</p>
                  </div>
                  <a href="/app" className="text-sm text-green-600 hover:text-green-700 font-medium">
                    ← Back to login
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
